import React from 'react'

import {
  DATA_ACCESS_REQUEST_STUDY_ID_MATCHED_ALERT,
  DATA_ACCESS_REQUEST_STUDY_ID_UNMATCHED_ALERT
} from 'modules/requests/data-access-requests/DataAccessRequestDetails/constants'
import * as styles from 'modules/requests/data-access-requests/DataAccessRequestDetails/getDataAccessRequestStudyIdEvaluationAlert.styles'
import { StudyIdConflictKind, StudyIdEvaluationOutcome } from 'services/data-access-requests/enums'
import type { StudyIdEvaluationConflict, StudyIdEvaluationResult } from 'services/data-access-requests/types'

export type DataAccessRequestStudyIdEvaluationAlertType = 'info' | 'warning'

export interface DataAccessRequestStudyIdEvaluationAlert {
  type: DataAccessRequestStudyIdEvaluationAlertType
  message: React.ReactNode
}

const formatStudyIdConflictMessage = (conflict: StudyIdEvaluationConflict): React.ReactNode => {
  if (conflict.kind === StudyIdConflictKind.SchemaAccess && conflict.workspace !== undefined) {
    return (
      <>
        The study ID already exists, it matches <styles.Emphasis>{conflict.workspace.name}</styles.Emphasis>, but{' '}
        <styles.Emphasis>{conflict.datasource.datasetName}</styles.Emphasis> is already shared with this workspace.
        Unshare the existing schema before approving this request.
      </>
    )
  }

  if (conflict.kind === StudyIdConflictKind.ApprovedDataAccessRequest) {
    const restrictionLabel = conflict.hasParticipantRestrictions ? 'restricted' : 'unrestricted'
    const requestSuffix =
      conflict.dataAccessRequestId !== undefined ? (
        <>
          {' '}
          (request <styles.Emphasis>{conflict.dataAccessRequestId}</styles.Emphasis>)
        </>
      ) : null

    return (
      <>
        The study ID is already used by an approved <styles.Emphasis>{restrictionLabel}</styles.Emphasis> data access
        request for <styles.Emphasis>{conflict.datasource.datasetName}</styles.Emphasis>
        {requestSuffix}. Resolve the existing request before approving this one.
      </>
    )
  }

  return (
    <>
      The study ID conflicts with existing access to{' '}
      <styles.Emphasis>{conflict.datasource.datasetName}</styles.Emphasis>. Resolve the conflict before approving this
      request.
    </>
  )
}

const formatStudyIdList = (studyIds: string[]): string => {
  if (studyIds.length <= 1) {
    return studyIds[0] ?? ''
  }

  if (studyIds.length === 2) {
    return `${studyIds[0]} and ${studyIds[1]}`
  }

  return `${studyIds.slice(0, -1).join(', ')}, and ${studyIds[studyIds.length - 1]}`
}

const uniqueStudyIds = (studyIds: string[]): string[] => {
  const seen = new Set<string>()

  return studyIds.filter(studyId => {
    if (seen.has(studyId)) {
      return false
    }

    seen.add(studyId)
    return true
  })
}

export const GROUP_STUDY_ID_MATCH_ALERT = 'Related requests in this group use this Study ID.'

export const getGroupStudyIdDifferenceAlert = (studyIds: string[]): string => {
  const label = studyIds.length === 1 ? 'Study ID' : 'Study IDs'

  return `Other requests in this group already use ${label} ${formatStudyIdList(
    studyIds
  )}. You can still approve with a different Study ID.`
}

const getOutcomeAlert = (
  evaluationResult: StudyIdEvaluationResult
): DataAccessRequestStudyIdEvaluationAlert | undefined => {
  switch (evaluationResult.outcome) {
    case StudyIdEvaluationOutcome.Matched:
      return {
        type: 'info',
        message: DATA_ACCESS_REQUEST_STUDY_ID_MATCHED_ALERT
      }
    case StudyIdEvaluationOutcome.Unmatched:
      return {
        type: 'info',
        message: DATA_ACCESS_REQUEST_STUDY_ID_UNMATCHED_ALERT
      }
    case StudyIdEvaluationOutcome.Conflict:
      return {
        type: 'warning',
        message: formatStudyIdConflictMessage(evaluationResult.conflict)
      }
    default:
      return undefined
  }
}

const getGroupStudyIdAlerts = (
  evaluationResult: StudyIdEvaluationResult,
  proposedStudyId: string,
  currentRequestId: string
): DataAccessRequestStudyIdEvaluationAlert[] => {
  if (!evaluationResult.hasGroupStudyIdAssigned || proposedStudyId.length === 0) {
    return []
  }

  const assignedStudyIds = uniqueStudyIds(
    evaluationResult.groupStudyIdAssignments
      .filter(assignment => assignment.dataAccessRequestId !== currentRequestId)
      .map(assignment => assignment.studyId)
  )
  const matchingStudyIds = assignedStudyIds.filter(studyId => studyId === proposedStudyId)
  const differingStudyIds = assignedStudyIds.filter(studyId => studyId !== proposedStudyId)
  const alerts: DataAccessRequestStudyIdEvaluationAlert[] = []

  if (matchingStudyIds.length > 0) {
    alerts.push({
      type: 'info',
      message: GROUP_STUDY_ID_MATCH_ALERT
    })
  }

  if (differingStudyIds.length > 0) {
    alerts.push({
      type: 'warning',
      message: getGroupStudyIdDifferenceAlert(differingStudyIds)
    })
  }

  return alerts
}

export const getDataAccessRequestStudyIdEvaluationAlerts = (
  evaluationResult: StudyIdEvaluationResult | undefined,
  proposedStudyId: string,
  currentRequestId: string
): DataAccessRequestStudyIdEvaluationAlert[] => {
  if (evaluationResult === undefined) {
    return []
  }

  const outcomeAlert = getOutcomeAlert(evaluationResult)
  const alerts = outcomeAlert === undefined ? [] : [outcomeAlert]

  return [...alerts, ...getGroupStudyIdAlerts(evaluationResult, proposedStudyId, currentRequestId)]
}
